import React, { useReducer, useEffect, useMemo, useRef, useCallback } from "react";
import {
    Box,
    Grid,
    IconButton,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Typography as MUITypography,
    Chip,
    Avatar,
    Stack,
    Tooltip,
    Slider,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    Menu,
    MenuItem,
    Divider,
} from "@mui/material";
import SettingsPhoneIcon from "@mui/icons-material/SettingsPhone";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import CircleIcon from "@mui/icons-material/Circle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MoreVertIcon from '@mui/icons-material/MoreVert'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import PauseIcon from '@mui/icons-material/Pause'
import PlayIcon from '@mui/icons-material/PlayArrow'
import CopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import * as moment from "moment";

// Componentes/Utils do seu projeto (iguais ao AgentOverview)
import Typography from "../../Typography";
import AvatarName from "../../AvatarName";
import api from "../../../api";
import defaultReducer from "../../../utils/defaultReducer";
import { getSession } from "../../../auth";
import { useTelephony } from "../../../contexts/TelephonyContext";

import "./styles.css";

/**
* SidebarStatus
* Mantém o mesmo "formato/padrão" do arquivo AgentOverview: constantes de sessão no topo,
* useReducer + defaultReducer, MUI, sem Tailwind e sem timer periódico.
*/

// ======= BLOCO DE SESSÃO (MESMO PADRÃO DO AgentOverview) =======
let interval = 0;
const user = getSession();
const enterprise = user?.profile?.enterprise;
const enterpriseId = enterprise?.id;
const userId = user?.id;
const agentId = user?.profile?.agent_id;
const has_voice = enterprise?.has_voice || enterprise?.whatsapp_calling;

const extensionUser = (() => {
    if (has_voice) {
        if (user?.profile?.agent?.external_username) {
            return user.profile.agent.external_username.split("_")[1] || "";
        } else if (user?.profile.agent?.preferred_interface) {
            return user?.profile.agent?.preferred_interface.split("_")[1] || "";
        }
    }
    return "";
})();

const agentUser = (() => {
    if (user?.profile?.instantvoice_agent_code) {
        return user.profile.instantvoice_agent_code;
    } else if (user?.profile?.agent?.user) {
        return user?.profile?.agent?.user.split("_")[1] || "";
    }
    return "";
})();

// =================== COMPONENTES LOCAIS ===================
const StatusDot = ({ color }) => (
    <CircleIcon fontSize="small" sx={{ color }} />
);

const Row = ({ icon, label, value, description=null }) => (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <MUITypography variant="caption" color="text.secondary">{label}</MUITypography>
            {description && (
                <MUITypography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {description}
                </MUITypography>
            )}
        </Stack>
        <MUITypography variant="body2" fontWeight={600}>{value}</MUITypography>
    </Stack>
);

// Mapeia status internos do seu TelephonyContext para rótulos e cor (igual ao que usamos antes)
const getStatusChip = (agentStatus = "LOGOFF", pauseReason = null) => {
    const map = {
        LOGIN: { label: "Logado", caption: "Ativo", color: "#17e917" }, // verde
        LOGOFF: { label: "Deslogado", caption: "Deslogado", color: "#ff0000ff" }, // vermelho
        PENDING_LOGOFF: { label: "Logoff pendente", caption: "Logoff pendente", color: "#f59e0b" }, // âmbar
        PAUSE: { label: pauseReason ? pauseReason : "Em pausa", caption: "Pausa", color: "#ffff00ff" }, // amarelo
        PENDING_PAUSE: { label: "Pausa pendente", caption: "Aguardando pausa", color: "#f59e0b" }, // âmbar
        UNAVAILABLE_WRAPUP: { label: "Pós-atendimento", caption: "Wrap-up", color: "#3b82f6" }, // azul
        UNAVAILABLE_QUARANTINE: { label: "Quarentena", caption: "Indisponível", color: "#ef4444" }, // vermelho
        LOGOFF_PAUSE: { label: "Deslogado", caption: "Logoff forçado", color: "#9ca3af" }, // cinza
    };
    return map[agentStatus] || { label: "Indefinido", caption: "—", color: "#9ca3af" };
};

// mm:ss para exibir “Maior Espera”
const formatWait = (seconds = 0) => {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

const isInstantVoiceIntegrated = () => {
    return (user?.profile.instantvoice_url
        && user?.profile.instantvoice_accountcode
        && user?.profile.instantvoice_api_key
        && user?.profile.instantvoice_agent_code
    )
}

// =================== SIDEBAR STATUS ===================
const SidebarStatus = ({ onLogout }) => {
    const {
        callStatus,
        registered = false,
        volume = 1,
        setVolume = () => { },
        muted = false,
        toggleMute = () => { },
    } = useTelephony?.() ?? {};
    
    const [state, setState] = useReducer(defaultReducer, {
        heartBeat: false,
        name: `${user?.first_name} ${user?.last_name}` || user?.profile?.full_name || "Operador",
        enterpriseName: enterprise?.name,
        agentStatus: user?.profile?.agent?.agentstatus,
        loginTime: user?.profile?.agent?.logints
            ? moment(new Date(user?.profile?.agent?.logints * 1000)).format("DD/MM HH:mm").toString()
            : "—",
        extensionUser,
        agentUser,
        registered,
        volume,
        muted,
        followMeEnabled: Boolean(user?.profile?.agent?.follow_me_enabled) || false,
        callsToday: 0,
        queuesCount: 0,
        queuesList: [],
        longestMaxWait: 0, // em segundos
        statusModalOpen: false,
        statusSelected: user?.profile?.agent?.agentstatus || "LOGOFF",
        
    });

    const [anchorElActions, setAnchorElActions] = React.useState(null)
    const [openDialog, setOpenDialog] = React.useState(false)
    const [pauseReasons, setPauseReasons] = React.useState([])
    const [pauseReason, setPauseReason] = React.useState()

    // ====== CONTROLES DO POLLING ======
    const timerRef = useRef(null);           // guarda o setInterval
    const eventRefreshTimerRef = useRef(null); // debounce para refresh via eventos
    const runningRef = useRef(false);        // evita overlap entre ciclos
    const mountedRef = useRef(false);        // evita duplicidade no StrictMode
    const abortRef = useRef(null);           // permite cancelar requests no unmount

    const safeGet = (obj, path, fallback = undefined) => {
        try { return path.split('.').reduce((o, k) => (o?.[k]), obj) ?? fallback; }
        catch { return fallback; }
    };

    useEffect(() => {
        getPauseReasons();
    }, []);

    // ====== TICK: chama duas APIs em paralelo a cada 30s (sem overlap) ======
    const tick = useCallback(async () => {
        if (runningRef.current) return;
        runningRef.current = true;
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const [overviewRes, agentinfoRes] = await Promise.allSettled([
                api.get(`/api/enterprises/${enterpriseId}/agents/${agentId}/overview/`, {
                    timeout: 15000, signal: controller.signal,
                }),                
                api.get(`/api/enterprises/${enterpriseId}/agents/${agentId}/agentinfo/`, {
                    timeout: 15000, signal: controller.signal,
                }),
            ]);

            // ---- OVERVIEW ----
            if (overviewRes.status === "fulfilled" && overviewRes.value.status === 200) {
                const d = overviewRes.value.data;                
                const finished = Array.isArray(d?.finished_contacts)
                    ? d.finished_contacts : [];
                const vozItem = finished.find(it => String(it?.name || "").trim().toLowerCase() === "voz");
                const callsToday = Number(vozItem?.value ?? 0);                
                const longestMaxWait = (() => {
                  const queues = Array.isArray(d?.voice_queues) ? d.voice_queues : [];
                  let max = 0;
                  for (const q of queues) {
                    const stats = Array.isArray(q?.stats) ? q.stats : [];
                    const item = stats.find(s => String(s?.name || "").trim().toLowerCase() === "maior espera");
                    const v = Number(item?.value ?? 0);
                    if (!Number.isNaN(v) && v > max) max = v;
                  }
                  return max;
                })();                
                setState({
                    name: `${user?.first_name} ${user?.last_name}`,
                    enterpriseName: enterprise?.name,                                        
                    hasVoice: has_voice,
                    extensionUser,
                    agentUser,
                    callsToday,
                    longestMaxWait,
                    queuesCount: Array.isArray(d?.voice_queues) ? d.voice_queues.length : 0,
                    queuesList: safeGet(d, 'voice_queues', []),
                });
            }

            // ---- AGENT_INFO ----
            if (agentinfoRes.status === "fulfilled" && agentinfoRes.value.status === 200) {
                const a = agentinfoRes.value.data;                
                setState({
                    agentStatus: a.agentstatus,
                    loginTime: a.logints
                        ? moment(new Date(a.logints * 1000)).format("DD/MM HH:mm").toString()
                        : "—",
                    pauseReason: a.reasons_reason || null,
                });                
            }

            if (overviewRes.status === "rejected") console.warn("overview error:", overviewRes.reason);
            if (agentinfoRes.status === "rejected") console.warn("agentinfo error:", agentinfoRes.reason);
        } catch (err) {
            if (err?.name !== "CanceledError" && err?.name !== "AbortError") console.error(err);
        } finally {
            runningRef.current = false;
        }
    }, [enterpriseId, agentId, setState]);

    // ====== EFFECT do polling (um intervalo, limpa no unmount, lida com StrictMode) ======
    useEffect(() => {
        if (mountedRef.current) return;              // evita 2x no StrictMode
        mountedRef.current = true;

        tick();                                      // primeira chamada imediata
        timerRef.current = setInterval(() => {
            if (document.visibilityState === "visible") tick();
        }, 30000);

        return () => {
            mountedRef.current = false;
            if (timerRef.current) clearInterval(timerRef.current);
            if (abortRef.current) abortRef.current.abort();
            if (eventRefreshTimerRef.current) clearTimeout(eventRefreshTimerRef.current);
        };
    }, [tick]);

    // Quando o contexto de telefonia mudar, refletimos no estado local (padrão AgentOverview usa setState/heartBeat)
    useEffect(() => {
        setState({ registered, volume, muted });
    }, [registered, volume, muted]);

    const chip = getStatusChip(state.agentStatus, state.pauseReason);
    const displayName = state.name;
    
    // Handlers Siga-me (UI only — plugue sua API aqui)
    const handleToggleFollowMe = async () => {
        // TODO: opcional -> await api.post('/followme/toggle', { enabled: !state.followMeEnabled })
        setState({ followMeEnabled: !state.followMeEnabled });
    };
    const handleConfigureFollowMe = () => {
        // TODO: abrir modal/config existente ou navegar
        // ex.: setOpenFollowMeModal(true)  |  navigate('/telephony/followme')
    };

    // ===== Modal de Status =====
    const openStatusModal = () => setState({ statusModalOpen: true, statusSelected: state.agentStatus });
    const closeStatusModal = () => setState({ statusModalOpen: false });
    const handleChangeSelectedStatus = (e) => setState({ statusSelected: e.target.value });
    const handleConfirmStatus = async () => {
        const to = state.statusSelected;
        // TODO: chame sua API/Context aqui, se existir:
        // await api.post('/agents/status', { status: to })
        setState({ agentStatus: to, statusModalOpen: false });
    };

    const handleActionsClick = (event) => {
        setAnchorElActions(event.currentTarget)
    }

    const handleActionsClose = () => {
        setAnchorElActions()
    }

    const handleInstantVoiceAgentLogin = () => {        
        let extension = ''        

        if (user.profile.agent.external_username) {
            extension = user.profile.agent.external_username;            
        } else if (user.profile.agent.preferred_interface) {
            extension = user.profile.agent.preferred_interface;
            extension = extension.split('_').pop();            
        }

        api.post(`api/IV/agents/${user.profile.id}/instantvoice_agent_login/`, {
            extension: extension
        }).then((res) => {
            if (res.status === 200) {
                handleActionsClose()
            }
        }).catch((err) => {})
    }

    const handleInstantVoiceAgentLogoff = () => {        
        api.post(`api/IV/agents/${user.profile.id}/instantvoice_agent_logoff/`)
            .then((res) => {
                if (res.status === 200) {
                    handleActionsClose()
                }
            })
            .catch((err) => {})
    }

    const getPauseReasons = () => {        
        api.get(`api/IV/agents/${user.profile.id}/instantvoice_pause_reasons/`)
        .then((res) => {
            if (res.status === 200) {
                setPauseReasons(res.data)
                handleActionsClose()
            }
        })
        .catch((err) => {})
    }

    const handleInstantVoiceAgentUnpause = () => {        
        api.post(`api/IV/agents/${user.profile.id}/instantvoice_agent_unpause/`)
        .then((res) => {
            if (res.status === 200) {
                handleActionsClose()
            }
        })
        .catch((err) => {})
    }

    const handlePauseReasons = () => {
        setOpenDialog(true)
        handleActionsClose()
    }

    const handleDialogClose = () => {
        setOpenDialog(false)
    }

    const handleReasonSubmit = () => {
        if (!pauseReason) {
            useNotification(
                'Atenção!',
                'Selecione o motivo da pausa.',
                'warning'
            );
            return
        }

        api.post(`api/IV/agents/${user.profile.id}/instantvoice_agent_pause/`, {
            reason: pauseReason
        }).then((res) => {
            if (res.status === 200) {
                setPauseReason()
                setOpenDialog(false)
            }
        }).catch((err) => { })
    }

    // ======== LISTENER DE EVENTOS DO WEBSOCKET (publicados pelo WebphoneMenu) ========
    // Mapeia eventos do socket para o status interno do agente
    const mapSocketTypeToStatus = (type, detail) => {
        switch (type) {
            case "LOGIN":
            case "LOGIN_COMMAND_OK":
            case "UNPAUSE":
            case "UNPAUSE_COMMAND_OK":
                return "LOGIN";
            case "LOGOUT":
            case "LOGOUT_COMMAND_OK":
                return "LOGOFF";
            case "PAUSE":
            //case "PAUSE_COMMAND_OK":
                return "PAUSE"; // se precisar, pegue reason de detail?.message?.reason
            default:
                return null;
        }
    };

    const onVoiceAny = useCallback((e) => {
        const { type, ...detail } = e.detail || {};        
        const mapped = mapSocketTypeToStatus(type, detail);
        let pauseReason = detail?.message?.direction || null;
        if (mapped) {
            if (mapped === 'PAUSE' && detail?.message?.direction) {
                console.log("Paused with reason:", detail.message.direction);
            }
            setState({ agentStatus: mapped, pauseReason });
            if (mapped === 'LOGIN') {
                // pequeno debounce para dar tempo do backend refletir o novo estado
                if (eventRefreshTimerRef.current) clearTimeout(eventRefreshTimerRef.current);
                eventRefreshTimerRef.current = setTimeout(() => {
                    // runningRef no tick já evita overlap com o polling
                    tick();
                }, 250);
            }
        }        
    }, [tick]);

    useEffect(() => {
        // Ouça o canal geral; se preferir, registre listeners por tipo (voice:LOGIN etc.)
        window.addEventListener("voice:any", onVoiceAny);
        return () => window.removeEventListener("voice:any", onVoiceAny);
    }, [onVoiceAny]);

    return (
        <Box className="agentoverview-sidebar">
            <Card>
                <CardHeader
                    sx={{ '& .MuiCardHeader-content': { minWidth: 0 } }}
                    avatar={<Avatar><AvatarName name={displayName} size="small" /></Avatar>}
                    title={
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            flexWrap="wrap"
                            sx={{ minWidth: 0 }}
                        >
                            <MUITypography
                                variant="subtitle1"
                                fontWeight={600}
                                noWrap
                                sx={{ minWidth: 0, maxWidth: '100%' }}
                                title={displayName}
                            >
                                {displayName}
                            </MUITypography>
                            {state.enterpriseName && (
                                <Tooltip title={state.enterpriseName}>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={state.enterpriseName}
                                        sx={{
                                            maxWidth: { xs: 120, sm: 180, md: 220 },
                                            flexShrink: 1,
                                            '& .MuiChip-label': {
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: 'block',
                                            },
                                        }}
                                    />
                                </Tooltip>
                            )}
                            <Tooltip title={state.registered ? "registrado" : "aguardando"}>
                                <IconButton
                                    size="small"
                                    sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                                >
                                    <SettingsPhoneIcon
                                        fontSize="small"
                                        color={state.registered ? "success" : "warning"}
                                    />
                                </IconButton>
                            </Tooltip>
                            <Chip
                                size="small"
                                color={state.registered ? "success" : "warning"}
                                variant="outlined"
                                icon={<SettingsPhoneIcon fontSize="small" />}
                                label={state.registered ? "registrado" : "aguardando"}
                                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                            />
                        </Stack>
                    }
                    subheader={user?.profile?.role || ""}                    
                />

                <CardContent>
                    <Stack spacing={2}>
                        {/* Status atual */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            // onClick={openStatusModal}
                            // sx={{ cursor: "pointer" }}
                            // title="Clique para alterar status"
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <StatusDot color={chip.color} />
                                <Box>
                                    <MUITypography variant="caption" color="text.secondary">Status</MUITypography>
                                    <MUITypography variant="body2" fontWeight={600}>{chip.label}</MUITypography>

                                    <Menu
                                        anchorEl={anchorElActions}
                                        keepMounted
                                        sx={{ zIndex: 2001 }}
                                        open={Boolean(anchorElActions)}
                                        onClose={handleActionsClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                    >
                                        {isInstantVoiceIntegrated () && 
                                            <MenuItem onClick={() => handleInstantVoiceAgentLogin()}>
                                                <LoginIcon style={{ marginRight: 8 }} />
                                                Login
                                            </MenuItem>
                                        }
                                        {isInstantVoiceIntegrated() && 
                                            <MenuItem onClick={handleInstantVoiceAgentLogoff}>
                                                <LogoutIcon style={{ marginRight: 8 }} />
                                                Logout
                                            </MenuItem>
                                        }
                                        <MenuItem onClick={handlePauseReasons}>
                                            <PauseIcon style={{ marginRight: 8 }} />
                                            Pausa
                                        </MenuItem>
                                        <MenuItem onClick={handleInstantVoiceAgentUnpause}>
                                            <PlayIcon style={{ marginRight: 8 }} />
                                            Despausa
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            </Stack>
                            {/* <Chip size="small" label={chip.caption} variant="outlined" /> */}
                            {/* Ações */}
                            <IconButton onClick={handleActionsClick}>
                                <MoreVertIcon className="toolbar-icon" />
                            </IconButton>
                        </Stack>

                        {/* Sessão */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <MUITypography variant="body2" color="text.secondary">
                                Login: <b>{state.loginTime}</b>
                            </MUITypography>
                        </Stack>

                        {/* Identificação */}
                        <Stack spacing={1}>
                            <Row icon={<PersonOutlineIcon fontSize="small" color="action" />} label="Operador" value={state.agentUser || "—"} />
                            <Row icon={<SettingsPhoneIcon fontSize="small" color="action" />} label="Ramal" value={state.extensionUser || "—"} />
                        </Stack>

                        {/* Siga-me */}
                        {/* <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <MUITypography variant="caption" color="text.secondary">
                                Siga-me
                            </MUITypography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Switch
                                    size="small"
                                    checked={Boolean(state.followMeEnabled)}
                                    onChange={handleToggleFollowMe}
                                />
                                <Button size="small" variant="outlined" onClick={handleConfigureFollowMe}>
                                    Configurar
                                </Button>
                            </Stack>
                        </Stack> */}

                        {/* Áudio */}
                        <Stack spacing={1}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <MUITypography variant="caption" color="text.secondary">Volume</MUITypography>
                                <Tooltip title={state.muted ? "Mudo: ligado" : "Mudo: desligado"}>
                                    <IconButton size="small" onClick={() => toggleMute?.()} aria-label="toggle mute">
                                        {state.muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <Slider
                                size="small"
                                value={Number(state.volume) * 100}
                                onChange={(_, v) => setVolume?.(Number(v) / 100)}
                                min={0}
                                max={100}
                                step={1}
                                aria-label="volume"
                            />
                        </Stack>

                        {/* Métricas (hoje) */}
                        <Stack spacing={1}>
                            <MUITypography variant="subtitle2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                                Estatísticas da sessão
                            </MUITypography>
                            <Row
                                icon={<SettingsPhoneIcon fontSize="small" color="action" />}
                                label="Atendidas"
                                value={state.callsToday}
                            />
                            <Row
                                icon={<PersonOutlineIcon fontSize="small" color="action" />}
                                label="Minhas Filas"
                                value={state.queuesCount}
                                description={state.queuesList.length > 0 ? `(${state.queuesList.map( (q) => q.name ).join(', ')})` : null}

                            />
                            <Row
                                icon={<AccessTimeIcon fontSize="small" color="action" />}
                                label="Maior Espera"
                                value={formatWait(state.longestMaxWait)}
                            />
                        </Stack>

                        {/* Ações */}
                        {/* <Stack direction="row" spacing={1}> */}
                            {/* <Button size="small" variant="outlined" onClick={() => { }}> */}
                                {/* Pausar/Retomar */}
                            {/* </Button> */}
                            {/* <Button size="small" color="error" variant="contained" startIcon={<PowerSettingsNewIcon />} onClick={onLogout}> */}
                                {/* Sair */}
                            {/* </Button> */}
                        {/* </Stack> */}
                    </Stack>
                </CardContent>
            </Card>

            {/* Modal de Status */}
            <Dialog open={openDialog} onClose={handleDialogClose}>
                <DialogTitle>Pausa</DialogTitle>
                <Divider/>
                <DialogContent>
                    <FormControl sx={{ mt: 1, minWidth: 250 }} size="small">
                        <InputLabel id="pause-reasons-list">Motivo da pausa</InputLabel>
                        <Select
                            labelId="pause-reasons-list"
                            value={pauseReason}
                            label="Motivo da pausa"
                            onChange={(event) => setPauseReason(event.target.value)}
                        >
                            {pauseReasons.map((each) => (
                                <MenuItem value={each.number}>{each.location}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ mr: 1 }}>
                    <Button onClick={handleDialogClose}>Cancelar</Button>
                    <Button onClick={handleReasonSubmit}>Pausar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SidebarStatus;
